import { useSelectorApp } from "../../lib/redux";
import {
  PLAN_BASIC,
  PLAN_STARTER,
  PLAN_STUDIO,
  PLAN_AGENCY,
  PLAN_ADVANCED,
  PLAN_PRO,
  PLAN_ENTERPRISE,
  PLAN_TYPE_PACKAGES,
  PLAN_TYPE_CREDITS,
  BENEFIT_KEYS,
  GB_IN_BYTES,
} from "../../utils/constants";
import {
  resolveCreditsPlanExtraBenefitKeys,
  normalizeAgencyPricingVersion,
  CREDIT_PRICING_VERSION_V4,
} from "../../utils/creditActionCosts";
import {
  ADS_SUBSCRIPTION_BENEFIT_CAMPAIGNS,
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS,
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISER,
  ADS_SUBSCRIPTION_ENTERPRISE_CAMPAIGNS_CUSTOM,
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS_CUSTOM,
  ADS_SUBSCRIPTION_BENEFIT_AI_CAMPAIGNS,
  ADS_SUBSCRIPTION_BENEFIT_AI_AUDIENCE_SEGMENTATION,
  ADS_SUBSCRIPTION_BENEFIT_AI_CONTENT,
  ADS_SUBSCRIPTION_BENEFIT_POSTS,
  ADS_SUBSCRIPTION_BENEFIT_AI_ADVANCED_INSIGHTS_AND_REPORTS,
  ADS_SUBSCRIPTION_BENEFIT_CREATIVES_CUSTOM,
  ADS_SUBSCRIPTION_BENEFIT_CREATIVES,
  ADS_SUBSCRIPTION_BENEFIT_VIDEOS,
  ADS_SUBSCRIPTION_BENEFIT_PRIORITY_SUPPORT,
  ADS_SUBSCRIPTION_BENEFIT_CAMPAIGN,
  PRICING_HERO_TITLE_PACKAGES,
  PRICING_HERO_TITLE_CREDITS,
  MODAL_SELECT_PACKAGE_ARIA_LABEL,
  MODAL_SELECT_CREDITS_ARIA_LABEL,
  ADS_SUBSCRIPTION_BENEFIT_CREDITS,
  ADS_SUBSCRIPTION_BENEFIT_CREDITS_PER_YEAR,
  ADS_SUBSCRIPTION_BENEFIT_CREDITS_PER_MONTH,
  ADS_SUBSCRIPTION_BENEFIT_USERS,
  ADS_SUBSCRIPTION_BENEFIT_USER,
  ADS_SUBSCRIPTION_BENEFIT_USERS_UP_TO,
  ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS_UP_TO,
  ADS_SUBSCRIPTION_BENEFIT_CHAT_SUPPORT,
  ADS_SUBSCRIPTION_BENEFIT_SUPPORT_CHAT_AND_EMAIL,
  ADS_SUBSCRIPTION_BENEFIT_UNLIMITED_SOCIAL_POSTS,
  ADS_SUBSCRIPTION_BENEFIT_WHITE_LABEL_OPTIONAL,
  ADS_SUBSCRIPTION_BENEFIT_STORAGE,
  ADS_SUBSCRIPTION_BENEFIT_STORAGE_CUSTOM,
} from "../../i18n/keysTranslations";

export const usePlanType = () => {
  const { typeOfPlans } = useSelectorApp((state) => state.payments);

  const currentTypeOfPlan = typeOfPlans || PLAN_TYPE_PACKAGES;

  const isTypeOfPlansLoaded = typeOfPlans !== null;

  return {
    typeOfPlans,
    currentTypeOfPlan,
    isTypeOfPlansLoaded,
  };
};

export const getPricingHeroTitle = (typeOfPlan, t) => {
  const MAP_PRICING_HERO_TITLE = {
    [PLAN_TYPE_PACKAGES]: t(PRICING_HERO_TITLE_PACKAGES),
    [PLAN_TYPE_CREDITS]: t(PRICING_HERO_TITLE_CREDITS),
  };

  return MAP_PRICING_HERO_TITLE[typeOfPlan];
};

export const getModalAriaLabel = (typeOfPlan, t) => {
  const MAP_MODAL_ARIA_LABEL = {
    [PLAN_TYPE_PACKAGES]: t(MODAL_SELECT_PACKAGE_ARIA_LABEL),
    [PLAN_TYPE_CREDITS]: t(MODAL_SELECT_CREDITS_ARIA_LABEL),
  };

  return MAP_MODAL_ARIA_LABEL[typeOfPlan];
};

const BASE_BENEFIT_GENERATORS = {
  [BENEFIT_KEYS.CAMPAIGNS]: (planId, benefits, multiplier) => ({
    translation:
      planId === PLAN_ENTERPRISE
        ? ADS_SUBSCRIPTION_ENTERPRISE_CAMPAIGNS_CUSTOM
        : benefits?.campaigns * multiplier === 1
          ? ADS_SUBSCRIPTION_BENEFIT_CAMPAIGN
          : ADS_SUBSCRIPTION_BENEFIT_CAMPAIGNS,
    value: benefits.campaigns,
  }),
  [BENEFIT_KEYS.CREATIVES]: (planId, benefits) =>
    benefits.creatives > 0 || planId === PLAN_ENTERPRISE
      ? {
          translation:
            planId === PLAN_ENTERPRISE
              ? ADS_SUBSCRIPTION_BENEFIT_CREATIVES_CUSTOM
              : ADS_SUBSCRIPTION_BENEFIT_CREATIVES,
          value: benefits.creatives,
        }
      : null,
  [BENEFIT_KEYS.VIDEOS]: (_planId, benefits) =>
    benefits?.videos
      ? {
          translation: ADS_SUBSCRIPTION_BENEFIT_VIDEOS,
          value: benefits.videos,
        }
      : null,
  [BENEFIT_KEYS.ADVERTISERS]: (planId, benefits) => ({
    translation:
      planId === PLAN_ENTERPRISE
        ? ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS_CUSTOM
        : benefits?.advertisers === 1
          ? ADS_SUBSCRIPTION_BENEFIT_ADVERTISER
          : ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS,
    value: benefits.advertisers,
  }),
  [BENEFIT_KEYS.STORAGE]: (planId, benefits) => {
    const storage = benefits?.storage;
    if (!storage && planId !== PLAN_ENTERPRISE) return null;
    const storageGb = Math.round(storage / GB_IN_BYTES);
    return {
      translation:
        planId === PLAN_ENTERPRISE
          ? ADS_SUBSCRIPTION_BENEFIT_STORAGE_CUSTOM
          : ADS_SUBSCRIPTION_BENEFIT_STORAGE,
      value: storageGb ?? 0,
    };
  },
  [BENEFIT_KEYS.MULTI_PLATFORM]: () => ({
    translation: ADS_SUBSCRIPTION_BENEFIT_AI_CAMPAIGNS,
  }),
  [BENEFIT_KEYS.AI_AUDIENCE]: () => ({
    translation: ADS_SUBSCRIPTION_BENEFIT_AI_AUDIENCE_SEGMENTATION,
  }),
  [BENEFIT_KEYS.AI_LOCATIONS]: () => ({
    translation: ADS_SUBSCRIPTION_BENEFIT_AI_CONTENT,
  }),
  [BENEFIT_KEYS.AI_TEXTS]: () => ({
    translation: ADS_SUBSCRIPTION_BENEFIT_POSTS,
  }),
  [BENEFIT_KEYS.AI_INSIGHTS]: () => ({
    translation: ADS_SUBSCRIPTION_BENEFIT_AI_ADVANCED_INSIGHTS_AND_REPORTS,
  }),
  [BENEFIT_KEYS.SUPPORT]: (planId) => {
    const finalBenefit = {
      [PLAN_BASIC]: ADS_SUBSCRIPTION_BENEFIT_CHAT_SUPPORT,
      [PLAN_STARTER]: ADS_SUBSCRIPTION_BENEFIT_CHAT_SUPPORT,
      [PLAN_STUDIO]: ADS_SUBSCRIPTION_BENEFIT_CHAT_SUPPORT,
      [PLAN_ADVANCED]: ADS_SUBSCRIPTION_BENEFIT_PRIORITY_SUPPORT,
      [PLAN_PRO]: ADS_SUBSCRIPTION_BENEFIT_PRIORITY_SUPPORT,
      [PLAN_AGENCY]: ADS_SUBSCRIPTION_BENEFIT_PRIORITY_SUPPORT,
      [PLAN_ENTERPRISE]: ADS_SUBSCRIPTION_BENEFIT_PRIORITY_SUPPORT,
    };
    return {
      translation: finalBenefit[planId],
    };
  },
  [BENEFIT_KEYS.CREDITS]: (planId, benefits) =>
    benefits?.credits
      ? {
          translation: ADS_SUBSCRIPTION_BENEFIT_CREDITS,
          value: benefits.credits,
        }
      : null,
  [BENEFIT_KEYS.USERS]: (planId, benefits) =>
    benefits?.users
      ? {
          translation:
            benefits.users === 1
              ? ADS_SUBSCRIPTION_BENEFIT_USER
              : ADS_SUBSCRIPTION_BENEFIT_USERS,
          value: benefits.users,
        }
      : null,
};

/** v4 credits UI (see product mock); onboarding benefits are intentionally omitted. */
const V4_BENEFIT_GENERATORS = {
  [BENEFIT_KEYS.CREDITS]: (_planId, benefits, multiplier) => {
    if (!benefits?.credits) return null;
    const isYearlyDisplay = multiplier > 1;
    return {
      translation: isYearlyDisplay
        ? ADS_SUBSCRIPTION_BENEFIT_CREDITS_PER_YEAR
        : ADS_SUBSCRIPTION_BENEFIT_CREDITS_PER_MONTH,
      value: benefits.credits,
    };
  },
  [BENEFIT_KEYS.VIDEOS]: (_planId, benefits) =>
    benefits?.videos
      ? {
          translation: ADS_SUBSCRIPTION_BENEFIT_VIDEOS,
          value: benefits.videos,
        }
      : null,
  [BENEFIT_KEYS.ADVERTISERS]: (planId, benefits) => {
    if (planId === PLAN_ENTERPRISE) {
      return {
        translation: ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS_CUSTOM,
      };
    }
    if (!benefits?.advertisers) return null;
    return {
      translation:
        benefits.advertisers === 1
          ? ADS_SUBSCRIPTION_BENEFIT_ADVERTISER
          : ADS_SUBSCRIPTION_BENEFIT_ADVERTISERS_UP_TO,
      value: benefits.advertisers,
    };
  },
  [BENEFIT_KEYS.USERS]: (planId, benefits) => {
    if (!benefits?.users || planId === PLAN_ENTERPRISE) return null;
    return {
      translation:
        benefits.users === 1
          ? ADS_SUBSCRIPTION_BENEFIT_USER
          : ADS_SUBSCRIPTION_BENEFIT_USERS_UP_TO,
      value: benefits.users,
    };
  },
  [BENEFIT_KEYS.STORAGE]: (planId, benefits) => {
    const storage = benefits?.storage;
    if (!storage && planId !== PLAN_ENTERPRISE) return null;
    const storageGb = Math.round(storage / GB_IN_BYTES);
    return {
      translation:
        planId === PLAN_ENTERPRISE
          ? ADS_SUBSCRIPTION_BENEFIT_STORAGE_CUSTOM
          : ADS_SUBSCRIPTION_BENEFIT_STORAGE,
      value: storageGb ?? 0,
    };
  },
  [BENEFIT_KEYS.UNLIMITED_SOCIAL_POSTS]: () => ({
    translation: ADS_SUBSCRIPTION_BENEFIT_UNLIMITED_SOCIAL_POSTS,
  }),
  [BENEFIT_KEYS.SUPPORT]: (planId) => {
    if (planId === PLAN_ENTERPRISE) {
      return { translation: ADS_SUBSCRIPTION_BENEFIT_PRIORITY_SUPPORT };
    }
    if (planId === PLAN_AGENCY) {
      return { translation: ADS_SUBSCRIPTION_BENEFIT_SUPPORT_CHAT_AND_EMAIL };
    }
    return { translation: ADS_SUBSCRIPTION_BENEFIT_CHAT_SUPPORT };
  },
  [BENEFIT_KEYS.WHITE_LABEL]: (planId) =>
    planId === PLAN_AGENCY
      ? { translation: ADS_SUBSCRIPTION_BENEFIT_WHITE_LABEL_OPTIONAL }
      : null,
};

const DEFAULT_BENEFITS_ORDER = [
  BENEFIT_KEYS.CAMPAIGNS,
  BENEFIT_KEYS.CREATIVES,
  BENEFIT_KEYS.ADVERTISERS,
  BENEFIT_KEYS.STORAGE,
  BENEFIT_KEYS.MULTI_PLATFORM,
  BENEFIT_KEYS.AI_AUDIENCE,
  BENEFIT_KEYS.AI_LOCATIONS,
  BENEFIT_KEYS.AI_TEXTS,
  BENEFIT_KEYS.AI_INSIGHTS,
  BENEFIT_KEYS.SUPPORT,
];

const CREDITS_BENEFITS_ORDER_V1_V3 = [
  BENEFIT_KEYS.CREDITS,
  BENEFIT_KEYS.ADVERTISERS,
  BENEFIT_KEYS.USERS,
  BENEFIT_KEYS.STORAGE,
  BENEFIT_KEYS.MULTI_PLATFORM,
  BENEFIT_KEYS.AI_AUDIENCE,
  BENEFIT_KEYS.AI_LOCATIONS,
  BENEFIT_KEYS.AI_TEXTS,
  BENEFIT_KEYS.AI_INSIGHTS,
  BENEFIT_KEYS.SUPPORT,
];

const CREDITS_BENEFITS_ORDER_V4 = [
  BENEFIT_KEYS.CREDITS,
  BENEFIT_KEYS.VIDEOS,
  BENEFIT_KEYS.ADVERTISERS,
  BENEFIT_KEYS.USERS,
  BENEFIT_KEYS.STORAGE,
  BENEFIT_KEYS.UNLIMITED_SOCIAL_POSTS,
  BENEFIT_KEYS.SUPPORT,
  BENEFIT_KEYS.WHITE_LABEL,
];

const PLAN_TYPE_BENEFITS_CONFIG = {
  [PLAN_TYPE_PACKAGES]: {
    order: DEFAULT_BENEFITS_ORDER,
    exclude: [],
    additional: [],
    planAdditional: {},
  },
  [PLAN_TYPE_CREDITS]: {
    order: CREDITS_BENEFITS_ORDER_V1_V3,
    exclude: [BENEFIT_KEYS.CAMPAIGNS, BENEFIT_KEYS.CREATIVES],
    additional: [],
    planAdditional: {},
  },
};

const CREDITS_BENEFITS_CONFIG_V4 = {
  order: CREDITS_BENEFITS_ORDER_V4,
  exclude: [
    BENEFIT_KEYS.CAMPAIGNS,
    BENEFIT_KEYS.CREATIVES,
    BENEFIT_KEYS.MULTI_PLATFORM,
    BENEFIT_KEYS.AI_AUDIENCE,
    BENEFIT_KEYS.AI_LOCATIONS,
    BENEFIT_KEYS.AI_TEXTS,
    BENEFIT_KEYS.AI_INSIGHTS,
  ],
  additional: [],
  planAdditional: {},
};

const isV4CreditsPricingUi = (typeOfPlan, creditPricingVersion, planId) =>
  typeOfPlan === PLAN_TYPE_CREDITS &&
  normalizeAgencyPricingVersion(creditPricingVersion) ===
    CREDIT_PRICING_VERSION_V4 &&
  planId !== PLAN_ENTERPRISE;

const resolveBenefitsConfig = (typeOfPlan, creditPricingVersion, planId) => {
  const base =
    PLAN_TYPE_BENEFITS_CONFIG[typeOfPlan] ||
    PLAN_TYPE_BENEFITS_CONFIG[PLAN_TYPE_PACKAGES];

  if (!isV4CreditsPricingUi(typeOfPlan, creditPricingVersion, planId)) {
    return base;
  }

  return CREDITS_BENEFITS_CONFIG_V4;
};

const resolveBenefitGenerators = (typeOfPlan, creditPricingVersion, planId) => {
  if (isV4CreditsPricingUi(typeOfPlan, creditPricingVersion, planId)) {
    return V4_BENEFIT_GENERATORS;
  }
  return BASE_BENEFIT_GENERATORS;
};

const buildCreditsBenefitsOrder = (
  config,
  creditPricingVersion,
  typeOfPlan
) => {
  if (
    typeOfPlan !== PLAN_TYPE_CREDITS ||
    normalizeAgencyPricingVersion(creditPricingVersion) ===
      CREDIT_PRICING_VERSION_V4
  ) {
    return config.order;
  }

  const extras = resolveCreditsPlanExtraBenefitKeys(creditPricingVersion);
  if (extras.length === 0) {
    return config.order;
  }

  const baseOrder = [...config.order];
  const creditsIdx = baseOrder.indexOf(BENEFIT_KEYS.CREDITS);
  return creditsIdx === -1
    ? [...extras, ...baseOrder]
    : [
        ...baseOrder.slice(0, creditsIdx + 1),
        ...extras,
        ...baseOrder.slice(creditsIdx + 1),
      ];
};

export const buildBenefitsFromConfig = (
  typeOfPlan,
  planId,
  benefits,
  multiplier,
  creditPricingVersion
) => {
  const config = resolveBenefitsConfig(
    typeOfPlan,
    creditPricingVersion,
    planId
  );
  const generators = resolveBenefitGenerators(
    typeOfPlan,
    creditPricingVersion,
    planId
  );
  const result = [];

  const order = buildCreditsBenefitsOrder(
    config,
    creditPricingVersion,
    typeOfPlan
  );

  const planAdditionalKeys = config.planAdditional?.[planId] || [];
  const seenKeys = new Set();

  [...order, ...planAdditionalKeys].forEach((benefitKey) => {
    if (config.exclude.includes(benefitKey) || seenKeys.has(benefitKey)) {
      return;
    }
    seenKeys.add(benefitKey);

    const generator = generators[benefitKey];
    if (!generator) {
      console.warn(`No generator found for benefit key: ${benefitKey}`);
      return;
    }

    const benefit = generator(planId, benefits, multiplier);
    if (benefit) {
      result.push(benefit);
    }
  });

  config.additional.forEach((benefitKey) => {
    const generator = generators[benefitKey];
    if (generator) {
      const benefit = generator(planId, benefits, multiplier);
      if (benefit) {
        result.push(benefit);
      }
    }
  });

  return result;
};
